import { create } from 'zustand';
import { clampLongMessageKb } from '../utils/chatComposeLimits';
import { applyAccentToDocument } from '../utils/accentColor';
import {
  DEFAULT_PREFERENCES,
  loadAppPreferences,
  saveAppPreferences,
  normalizeKeyboardShortcuts,
  clampMailCatcherPort,
  clampMailCatcherKeep,
} from '../services/appPreferencesStore';
import { normalizeDbIdleDisconnect } from '../extensions/dbIdleDisconnect';
import { normalizeDbTypeColors } from '../components/dbManager/dbTypeColors';
import { changeAppLanguage } from '../i18n';
import { SHORTCUT_COMMANDS, resolveShortcutKeys, keysEqual } from '../config/shortcuts';
import { isTauri } from '../utils/tauri';
import { applyLaunchAtLoginDefaultOnce } from '../services/launchAtLogin';

/**
 * Push the close-to-tray preference to the Rust side, which owns the actual
 * window-close interception. No-ops in the web build. Exported so startup can
 * sync the hydrated value once the app boots.
 */
export function syncCloseToTrayToBackend(enabled) {
  if (!isTauri()) return;
  import('@tauri-apps/api/core')
    .then(({ invoke }) => invoke('set_close_to_tray', { enabled }))
    .catch(() => {});
}

/**
 * Unbind any other editable command whose current binding equals `keys`, so a
 * combo is never shared by two commands. Mutates `overrides` and returns the
 * ids that were displaced. No-op when `keys` is falsy (unbound).
 */
function displaceConflicts(overrides, id, keys) {
  const displaced = [];
  if (!keys) return displaced;
  for (const cmd of SHORTCUT_COMMANDS) {
    if (cmd.id === id || !cmd.editable) continue;
    const existing = resolveShortcutKeys(cmd.id, overrides);
    if (existing && keysEqual(existing, keys)) {
      overrides[cmd.id] = null; // explicitly unbound
      displaced.push(cmd.id);
    }
  }
  return displaced;
}

function readWorkspaceTransitionSync() {
  try {
    const raw = localStorage.getItem('apiboo-app-preferences');
    if (!raw) return DEFAULT_PREFERENCES.workspaceTransition;
    const parsed = JSON.parse(raw);
    return parsed.workspaceTransition === false ? false : true;
  } catch {
    return DEFAULT_PREFERENCES.workspaceTransition;
  }
}

function applyDensity(density) {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  el.classList.remove('density-compact', 'density-balanced', 'density-spacious');
  if (density) el.classList.add(`density-${density}`);
}

function applyUiFontSize(size) {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--ui-font-size', `${size}px`);
}

function applySidebarPosition(pos) {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('sidebar-right', pos === 'right');
}

export function resolveThemeIsDark(theme) {
  if (theme === 'dark') return true;
  if (theme === 'system') {
    return typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  return false;
}

function applyThemeClass(theme) {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  el.classList.add('theme-switching');
  el.classList.toggle('dark', resolveThemeIsDark(theme));
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      el.classList.remove('theme-switching');
    });
  });
}

const WHITE_PRESET = '#f4f4f5';
const DARK_PRESET  = '#45444b';

function swapContrastAccentIfNeeded(willBeDark, get, set) {
  const current = get().accentColor;
  // dark preset in dark mode = invisible → swap to white
  // white preset in light mode = invisible → swap to dark
  const target = willBeDark && current === DARK_PRESET ? WHITE_PRESET
               : !willBeDark && current === WHITE_PRESET ? DARK_PRESET
               : null;
  if (!target) return;
  const applied = applyAccentToDocument(target, get().accentFg);
  set({ accentColor: applied });
  saveAppPreferences({ ...get(), accentColor: applied });
}

let systemThemeUnlisten = null;

function watchSystemTheme(theme, store, onThemeChange) {
  if (systemThemeUnlisten) {
    systemThemeUnlisten();
    systemThemeUnlisten = null;
  }
  if (theme !== 'system' || typeof window === 'undefined') return;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => {
    applyThemeClass(store.getState().theme);
    onThemeChange?.(resolveThemeIsDark('system'));
  };
  mq.addEventListener('change', handler);
  systemThemeUnlisten = () => mq.removeEventListener('change', handler);
}

export const usePreferencesStore = create((set, get) => ({
  // ── existing ──
  theme: DEFAULT_PREFERENCES.theme,
  language: DEFAULT_PREFERENCES.language,
  accentColor: DEFAULT_PREFERENCES.accentColor,
  workspaceTransition: readWorkspaceTransitionSync(),
  hydrated: false,
  // ── General ──
  timeFormat: DEFAULT_PREFERENCES.timeFormat,
  restoreLastSession: DEFAULT_PREFERENCES.restoreLastSession,
  checkUpdatesFrequency: DEFAULT_PREFERENCES.checkUpdatesFrequency,
  // Not UI settings — carried here only so `updatePreference` (which persists
  // `{ ...storeState, [key]: value }`) does not blank them on the next Settings
  // change. A key absent from this store is absent from that spread, and
  // `normalizePreferences` writes what it is given.
  skippedUpdateVersion: DEFAULT_PREFERENCES.skippedUpdateVersion,
  updatePromptLastShownDay: DEFAULT_PREFERENCES.updatePromptLastShownDay,
  sendUsageData: DEFAULT_PREFERENCES.sendUsageData,
  usageDataAsked: DEFAULT_PREFERENCES.usageDataAsked,
  linkPreviews: DEFAULT_PREFERENCES.linkPreviews,
  readReceipts: DEFAULT_PREFERENCES.readReceipts,
  // ── Notifications ──
  desktopNotifications: DEFAULT_PREFERENCES.desktopNotifications,
  notificationSound: DEFAULT_PREFERENCES.notificationSound,
  closeToTray: DEFAULT_PREFERENCES.closeToTray,
  launchAtLoginApplied: DEFAULT_PREFERENCES.launchAtLoginApplied,
  // ── Appearance extras ──
  density: DEFAULT_PREFERENCES.density,
  sidebarPosition: DEFAULT_PREFERENCES.sidebarPosition,
  uiFontSize: DEFAULT_PREFERENCES.uiFontSize,
  showNavTooltips: DEFAULT_PREFERENCES.showNavTooltips,
  accentFg: DEFAULT_PREFERENCES.accentFg,
  // ── Chat extension ──
  chatLongMessageAsFile: DEFAULT_PREFERENCES.chatLongMessageAsFile,
  chatLongMessageKb: DEFAULT_PREFERENCES.chatLongMessageKb,
  chatShowSizeIndicator: DEFAULT_PREFERENCES.chatShowSizeIndicator,
  // ── AI assistant ──
  aiReadResponses: DEFAULT_PREFERENCES.aiReadResponses,
  // ── Terminal extension ──
  terminalFontSize: DEFAULT_PREFERENCES.terminalFontSize,
  terminalCloseOnExit: DEFAULT_PREFERENCES.terminalCloseOnExit,
  terminalTheme: DEFAULT_PREFERENCES.terminalTheme,
  terminalOptionAsMeta: DEFAULT_PREFERENCES.terminalOptionAsMeta,
  terminalSendCtrlK: DEFAULT_PREFERENCES.terminalSendCtrlK,
  terminalNotifyLongCommands: DEFAULT_PREFERENCES.terminalNotifyLongCommands,
  terminalNotifySeconds: DEFAULT_PREFERENCES.terminalNotifySeconds,
  // ── FTP/SFTP extension ──
  ftpShowHiddenFiles: DEFAULT_PREFERENCES.ftpShowHiddenFiles,
  // ── Mail catcher extension ──
  mailCatcherPort: DEFAULT_PREFERENCES.mailCatcherPort,
  mailCatcherAutoStart: DEFAULT_PREFERENCES.mailCatcherAutoStart,
  mailCatcherKeep: DEFAULT_PREFERENCES.mailCatcherKeep,
  // ── DB Manager extension ──
  dbIdleDisconnect: DEFAULT_PREFERENCES.dbIdleDisconnect,
  dbTypeColors: DEFAULT_PREFERENCES.dbTypeColors,
  // ── Editor ──
  editorFontFamily: DEFAULT_PREFERENCES.editorFontFamily,
  editorFontSize: DEFAULT_PREFERENCES.editorFontSize,
  editorTabSize: DEFAULT_PREFERENCES.editorTabSize,
  wordWrap: DEFAULT_PREFERENCES.wordWrap,
  stickyScroll: DEFAULT_PREFERENCES.stickyScroll,
  defaultResponseView: DEFAULT_PREFERENCES.defaultResponseView,
  responseSizeLimitMb: DEFAULT_PREFERENCES.responseSizeLimitMb,
  // ── Requests ──
  requestTimeout: DEFAULT_PREFERENCES.requestTimeout,
  followRedirects: DEFAULT_PREFERENCES.followRedirects,
  httpVersion: DEFAULT_PREFERENCES.httpVersion,
  useSystemProxy: DEFAULT_PREFERENCES.useSystemProxy,
  proxyUrl: DEFAULT_PREFERENCES.proxyUrl,
  // ── Keyboard ──
  keyboardShortcuts: DEFAULT_PREFERENCES.keyboardShortcuts,

  hydrateFromDisk: (prefs) => {
    const validThemes = ['light', 'dark', 'system'];
    const next = {
      theme: validThemes.includes(prefs.theme) ? prefs.theme : 'system',
      language: prefs.language === 'de' ? 'de' : 'en',
      accentColor: prefs.accentColor,
      workspaceTransition: prefs.workspaceTransition === false ? false : true,
      hydrated: true,
      timeFormat: prefs.timeFormat ?? DEFAULT_PREFERENCES.timeFormat,
      restoreLastSession: prefs.restoreLastSession ?? DEFAULT_PREFERENCES.restoreLastSession,
      checkUpdatesFrequency: prefs.checkUpdatesFrequency ?? DEFAULT_PREFERENCES.checkUpdatesFrequency,
      skippedUpdateVersion: prefs.skippedUpdateVersion ?? DEFAULT_PREFERENCES.skippedUpdateVersion,
      updatePromptLastShownDay:
        prefs.updatePromptLastShownDay ?? DEFAULT_PREFERENCES.updatePromptLastShownDay,
      // Defaults OFF and needs an answer (normalizeUsageConsent): `=== true` only.
      sendUsageData: prefs.usageDataAsked === true && prefs.sendUsageData === true,
      usageDataAsked: prefs.usageDataAsked === true,
      linkPreviews: prefs.linkPreviews === true,
      readReceipts: prefs.readReceipts === false ? false : true,
      desktopNotifications: prefs.desktopNotifications === false ? false : true,
      notificationSound: prefs.notificationSound === false ? false : true,
      closeToTray: prefs.closeToTray === false ? false : true,
      launchAtLoginApplied: prefs.launchAtLoginApplied === true,
      density: prefs.density ?? DEFAULT_PREFERENCES.density,
      sidebarPosition: prefs.sidebarPosition ?? DEFAULT_PREFERENCES.sidebarPosition,
      uiFontSize: prefs.uiFontSize ?? DEFAULT_PREFERENCES.uiFontSize,
      showNavTooltips: prefs.showNavTooltips === false ? false : true,
      accentFg: prefs.accentFg ?? DEFAULT_PREFERENCES.accentFg,
      // `=== false` rather than `??`: a stored `false` must survive hydration,
      // and these default ON — `prefs.x ?? true` would be correct too, but the
      // surrounding booleans all use this form and drifting from it is how a
      // default silently flips.
      chatLongMessageAsFile: prefs.chatLongMessageAsFile === false ? false : true,
      chatLongMessageKb: clampLongMessageKb(
        prefs.chatLongMessageKb ?? DEFAULT_PREFERENCES.chatLongMessageKb
      ),
      chatShowSizeIndicator: prefs.chatShowSizeIndicator === false ? false : true,
      // Defaults OFF — `=== true` so only an explicit opt-in is honoured.
      aiReadResponses: prefs.aiReadResponses === true,
      terminalFontSize: prefs.terminalFontSize ?? DEFAULT_PREFERENCES.terminalFontSize,
      terminalCloseOnExit: prefs.terminalCloseOnExit ?? DEFAULT_PREFERENCES.terminalCloseOnExit,
      terminalTheme: prefs.terminalTheme ?? DEFAULT_PREFERENCES.terminalTheme,
      terminalOptionAsMeta: prefs.terminalOptionAsMeta === true,
      terminalSendCtrlK: prefs.terminalSendCtrlK === true,
      terminalNotifyLongCommands: prefs.terminalNotifyLongCommands === false ? false : true,
      terminalNotifySeconds: prefs.terminalNotifySeconds ?? DEFAULT_PREFERENCES.terminalNotifySeconds,
      // Defaults ON — `=== false` so a stored opt-out survives hydration.
      ftpShowHiddenFiles: prefs.ftpShowHiddenFiles === false ? false : true,
      mailCatcherPort: clampMailCatcherPort(prefs.mailCatcherPort ?? DEFAULT_PREFERENCES.mailCatcherPort),
      // Defaults OFF — `=== true` so only an explicit opt-in survives hydration.
      mailCatcherAutoStart: prefs.mailCatcherAutoStart === true,
      mailCatcherKeep: clampMailCatcherKeep(prefs.mailCatcherKeep ?? DEFAULT_PREFERENCES.mailCatcherKeep),
      dbIdleDisconnect: normalizeDbIdleDisconnect(prefs.dbIdleDisconnect),
      dbTypeColors: normalizeDbTypeColors(prefs.dbTypeColors),
      editorFontFamily: prefs.editorFontFamily ?? DEFAULT_PREFERENCES.editorFontFamily,
      editorFontSize: prefs.editorFontSize ?? DEFAULT_PREFERENCES.editorFontSize,
      editorTabSize: prefs.editorTabSize ?? DEFAULT_PREFERENCES.editorTabSize,
      wordWrap: prefs.wordWrap ?? DEFAULT_PREFERENCES.wordWrap,
      stickyScroll: prefs.stickyScroll ?? DEFAULT_PREFERENCES.stickyScroll,
      defaultResponseView: prefs.defaultResponseView ?? DEFAULT_PREFERENCES.defaultResponseView,
      responseSizeLimitMb: prefs.responseSizeLimitMb ?? DEFAULT_PREFERENCES.responseSizeLimitMb,
      requestTimeout: prefs.requestTimeout ?? DEFAULT_PREFERENCES.requestTimeout,
      followRedirects: prefs.followRedirects ?? DEFAULT_PREFERENCES.followRedirects,
      httpVersion: prefs.httpVersion ?? DEFAULT_PREFERENCES.httpVersion,
      useSystemProxy: prefs.useSystemProxy ?? DEFAULT_PREFERENCES.useSystemProxy,
      proxyUrl: prefs.proxyUrl ?? DEFAULT_PREFERENCES.proxyUrl,
      keyboardShortcuts: normalizeKeyboardShortcuts(prefs.keyboardShortcuts),
    };
    set(next);
    applyThemeClass(next.theme);
    applyAccentToDocument(next.accentColor, next.accentFg);
    changeAppLanguage(next.language);
    applyDensity(next.density);
    applyUiFontSize(next.uiFontSize);
    applySidebarPosition(next.sidebarPosition);
    watchSystemTheme(next.theme, { getState: get });
    // Push the persisted close-to-tray choice to Rust (owns window close).
    syncCloseToTrayToBackend(next.closeToTray);
    // Open at sign-in: the default is applied once per device, then left to the user.
    applyLaunchAtLoginDefaultOnce(next, () => get().updatePreference('launchAtLoginApplied', true));
  },

  setTheme: (theme) => {
    const validThemes = ['light', 'dark', 'system'];
    const next = validThemes.includes(theme) ? theme : 'light';
    set({ theme: next });
    applyThemeClass(next);
    swapContrastAccentIfNeeded(resolveThemeIsDark(next), get, set);
    watchSystemTheme(next, { getState: get }, (isDark) => swapContrastAccentIfNeeded(isDark, get, set));
    const s = get();
    saveAppPreferences({ ...s, theme: next }, { immediate: true });
  },

  setLanguage: (language) => {
    const next = language === 'de' ? 'de' : 'en';
    set({ language: next });
    changeAppLanguage(next);
    const s = get();
    saveAppPreferences({ ...s, language: next });
  },

  setAccentColor: (accentColor) => {
    const applied = applyAccentToDocument(accentColor, get().accentFg);
    set({ accentColor: applied });
    const s = get();
    saveAppPreferences({ ...s, accentColor: applied });
  },

  setWorkspaceTransition: (enabled) => {
    const next = Boolean(enabled);
    set({ workspaceTransition: next });
    const s = get();
    saveAppPreferences({ ...s, workspaceTransition: next });
  },

  updatePreference: (key, value) => {
    set({ [key]: value });
    if (key === 'density') applyDensity(value);
    if (key === 'uiFontSize') applyUiFontSize(value);
    if (key === 'sidebarPosition') applySidebarPosition(value);
    if (key === 'accentFg') applyAccentToDocument(get().accentColor, value);
    if (key === 'closeToTray') syncCloseToTrayToBackend(value);
    const s = get();
    saveAppPreferences({ ...s, [key]: value });
  },

  /**
   * The one-time "Help us improve Apiboo?" answer (UsageConsentCard), or the
   * Settings › Privacy toggle: either way the question is now answered, so it
   * is never asked again. Written at once — a crash right after must not
   * bring the question back.
   */
  answerUsageConsent: (share) => {
    const next = { sendUsageData: share === true, usageDataAsked: true };
    set(next);
    saveAppPreferences({ ...get(), ...next }, { immediate: true });
  },

  /**
   * Bind `keys` to command `id`. If the combo is already held by another
   * editable command, that command is auto-unbound (set to null).
   * @returns {string[]} ids of commands that were displaced (unbound).
   */
  setKeyboardShortcut: (id, keys) => {
    const overrides = { ...get().keyboardShortcuts };
    const displaced = displaceConflicts(overrides, id, keys);
    overrides[id] = keys;
    set({ keyboardShortcuts: overrides });
    saveAppPreferences({ ...get(), keyboardShortcuts: overrides });
    return displaced;
  },

  resetKeyboardShortcut: (id) => {
    const overrides = { ...get().keyboardShortcuts };
    delete overrides[id];
    // Restoring the default may collide with a command the user remapped to it,
    // so re-run conflict resolution and unbind any other command holding it.
    const keys = resolveShortcutKeys(id, overrides);
    const displaced = displaceConflicts(overrides, id, keys);
    set({ keyboardShortcuts: overrides });
    saveAppPreferences({ ...get(), keyboardShortcuts: overrides });
    return displaced;
  },

  resetAllKeyboardShortcuts: () => {
    set({ keyboardShortcuts: {} });
    saveAppPreferences({ ...get(), keyboardShortcuts: {} });
  },

  toggleTheme: () => {
    const next = get().theme === 'dark' ? 'light' : 'dark';
    get().setTheme(next);
  },
}));

export async function initPreferencesFromDisk() {
  const prefs = await loadAppPreferences();
  usePreferencesStore.getState().hydrateFromDisk(prefs);
  return prefs;
}

/** @deprecated Use usePreferencesStore — kept for CodeEditor / ResponseViewer */
export function useThemeStore(selector) {
  return usePreferencesStore((s) =>
    selector({ theme: s.theme, setTheme: s.setTheme, toggleTheme: s.toggleTheme })
  );
}
